/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import { SyntheticEvent, useContext, useEffect, useRef } from "react";
import { FormsIds } from "../../Utils/Constants";
import { Autocomplete, Grid, InputAdornment, TextField } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Vocabulary } from "../../Utils/Vocabulary";
import { yupResolver } from "@hookform/resolvers/yup";
import { ClassValidationSchema } from "../../Utils/Models";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { getValueForAutocomplete, onFormError } from "../../Utils/Utils";
import ClassIcon from "@mui/icons-material/Class";
import genericStyles from "../../Styles/genericStyles.module.css";
import { CRUDContext } from "./ClassesTable";
import { endpoints } from "../../Utils/UrlEnum";
import { updateData } from "../../Services/updateData";
import { postData } from "../../Services/postData";

export default function ClassForm(props: any) {
  const formRef = useRef<HTMLFormElement>(null);
  const memo = useCustomMemo();
  const orderTypes = memo?.storage?.orderTypes;
  const workPoints = memo?.storage.workPoints || [];
  const firstUpdate = useRef(true);
  const ref: any = useRef();
  // Use the provided vehicle or fallback to the CRUDContext
  const classContext: any = useContext(
    props.selectedProduct ? props.selectedProduct : CRUDContext
  );
  const restrictedWorkPointId = props?.workPointId;

  // Initialize the form with Yup validation and default values
  const { control, setValue, handleSubmit, reset } = useForm({
    resolver: yupResolver(ClassValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {},
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  const formData = useWatch({ control });

  // Update context when form data changes
  useEffect(() => {
    if (formData && !firstUpdate.current) {
      props.setContextValue({ ...formData });
    }
  }, [formData]);

  /**
   *
   */
  useEffect(() => {
    if (classContext.name && classContext.name !== "") {
      reset(classContext);
    } else if (props?.restrictByWorkPoint && restrictedWorkPointId) {
      // Set workpoint from localStorage for restricted users
      setValue("workPointId", restrictedWorkPointId);
    }
  }, []);


  /**
   *
   */
  useEffect(
    () => {
      if (firstUpdate.current) {
        firstUpdate.current = false;
        return;
      }
      if (!firstUpdate.current) ref.current.submit();
    },
    [
      // props.shouldHandleSubmit
    ]
  );

  // Update an existing element
  const updateElement = async (model: any) => {
    const url = `${endpoints.classesEndPoint}/${classContext?._id}`;

    try {
      const res = (await updateData(url, model)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
      props.setContextValue([]);
      props.onClose();
    } catch (error) {
      console.error("Error updating vehicle:", error);
    }
  };

  /**
   * Create a new class and add it to the database.
   * @param {object} model The new class data.
   * @returns {Promise<void>}
   */
  const createElement = async (model: any) => {
    const url = `${endpoints.classesEndPoint}`;

    try {
      const res = (await postData(url, model)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
      props.onClose();
      props.setContextValue([]);
    } catch (error) {
      console.error("Error creating class:", error);
    }
  };

  /**
   *
   * @param model
   */
  const onSubmit = (model: any) => {
    classContext._id ? updateElement(model) : createElement(model);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      method="get"
      ref={formRef}
      id={FormsIds.classForm}
      className={genericStyles.modalForm}
    >
      <Grid className={genericStyles.formContainer} container spacing={5}>
        <Grid item xs={12} sm={12} md={6}>
          <Controller
            name={"name"}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value}
                id="name"
                name="name"
                label={Vocabulary.name}
                variant={"standard"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {<ClassIcon />}
                    </InputAdornment>
                  ),
                }}
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller
            name="typeId"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                disablePortal
                disableClearable
                clearOnBlur={true}
                options={orderTypes ?? []}
                getOptionLabel={(option: any) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.name == value?.name
                }
                onChange={(
                  event: SyntheticEvent<Element, Event>,
                  newValue: any
                ) => {
                  setValue("typeId", newValue?._id);
                }}
                value={getValueForAutocomplete(orderTypes, field?.value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    value={field?.value || " "}
                    label={Vocabulary.selectClassType}
                    error={!!error}
                    helperText={error?.message}
                    variant="standard"
                    fullWidth
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller
            name="workPointId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                disablePortal
                disableClearable
                clearOnBlur={true}
                disabled={props?.restrictByWorkPoint}
                options={workPoints} // List of work points
                getOptionLabel={(option) => option?.name || ""} // Show name in dropdown
                isOptionEqualToValue={(option, value) =>
                  option?._id === (value?._id || value)
                }
                value={
                  getValueForAutocomplete(workPoints, field?.value)
                } // Ensure correct value selection
                onChange={(_, selectedOption) =>
                  field.onChange(selectedOption?._id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!error}
                    helperText={error?.message}
                    label={Vocabulary.workPoint}
                    variant="standard"
                    fullWidth
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>
    </form>
  );
}
